<template>
  <div class="content">
    <div class="chat_container">
      <div class="chat_sidebar">
        <div class="sidebar_header">
          <h3>客户列表</h3>
        </div>
        <div class="search_box">
          <el-input v-model="searchKeyword" placeholder="搜索客户名称" prefix-icon="Search" clearable size="small" />
        </div>

        <div class="contact_list" ref="contactListRef" @scroll="handleContactListScroll">
          <div v-for="(contact, index) in filteredContactList" :key="contact.Id"
            :class="['contact_item', { active: currentContactId === contact.Id }]"
            @click="selectContact(contact, index)">
            <div class="contact_avatar">
              <img :src="defaultAvatar" alt="avatar">
              <span v-if="contact.HasUnreadMsg > 0" class="unread_badge">{{ contact.HasUnreadMsg }}</span>
            </div>
            <div class="contact_info">
              <div class="contact_name_row">
                <div class="contact_name">{{ contact.Nickname }}</div>
                <div class="contact_time" v-if="contact.LastTime">{{ formatMessageTime(contact.LastTime) }}</div>
              </div>
              <div class="contact_last_msg" v-if="contact.LastMsg.includes('upload/chat')">[图片]</div>
              <div class="contact_last_msg" v-else>{{ contact.LastMsg || '暂无消息' }}</div>
            </div>
          </div>

          <div v-if="isLoadingContacts && !searchKeyword.trim()" class="loading_contacts">
            <span>加载中...</span>
          </div>

          <div v-if="!hasMoreContacts && contactList.length > 0 && !searchKeyword.trim()" class="no_more_contacts">
            <span>没有更多客户了</span>
          </div>

          <div v-if="filteredContactList.length === 0 && !isLoadingContacts" class="no_result">
            <p>{{ searchKeyword ? '未找到相关客户' : '暂无客户' }}</p>
          </div>
        </div>
      </div>

      <div class="chat_main">
        <div class="chat_header">
          <div class="header_info">
            <img :src="defaultAvatar" class="header_avatar">
            <div class="header_text">
              <el-link type="primary" @click="userDrawerVisible = true">
                <div class="header_name">{{ currentContact?.Nickname || '请选择客户' }}</div>
              </el-link>
              <div class="belonging">{{ currentContact?.NamePath }}</div>
            </div>
          </div>
        </div>

        <div class="message_area" ref="messageAreaRef">
          <div v-if="!currentContact" class="empty_state">
            <p>请选择一个客户开始聊天</p>
          </div>
          <div v-else class="message_list">
            <div v-if="isLoadingHistory" class="loading_more">
              <span>加载中...</span>
            </div>
            <div v-else-if="!hasMoreHistory && messageList.length > 0" class="no_more_history">
              <span>没有更多消息了</span>
            </div>
            <div v-for="msg in messageList" :key="msg.Id"
              :class="['message_item', msg.SenderId.includes('cs') ? 'message_self' : 'message_other']">
              <div class="message_avatar">
                <img v-if="msg.SenderId.includes('user')" :src="defaultAvatar" alt="avatar" />
                <img v-else-if="msg.SenderId.includes('cs')" :src="selfAvatar" alt="avatar" />
              </div>
              <div class="message_content">
                <div class="message_info">
                  <span class="message_name">{{ msg.SenderId.includes('cs') ? '我' : currentContact.Nickname }}</span>
                  <span class="message_time">{{ msg.CreatedAtStr }}</span>
                </div>

                <div v-if="msg.MsgType == 1" class="message_bubble">
                  {{ msg.Msg }}
                </div>

                <div v-else-if="msg.MsgType == 2" class="message_image">
                  <img :src="'data:image/bmp;base64,' + msg.Msg" alt="图片" @click="previewImage(msg.Msg)">
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="input_area" v-if="currentContact">
          <div class="input_toolbar">
            <el-button type="primary" size="small" plain @click="triggerImageUpload">
              <i class="el-icon-picture"></i> 发送图片
            </el-button>
            <input type="file" ref="imageInputRef" accept="image/*" style="display: none" @change="handleImageSelect">
          </div>

          <div class="input_box">
            <textarea v-model="inputMessage" placeholder="请输入消息..." @keydown.enter.ctrl="sendMessage"
              rows="3"></textarea>
          </div>

          <div class="input_footer">
            <span class="tip_text">按 Ctrl+Enter 发送</span>
            <el-button type="primary" @click="sendMessage" :disabled="!inputMessage.trim()">
              发送
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <el-dialog v-model="imagePreviewVisible" width="60%" :show-close="true">
      <img :src="'data:image/bmp;base64,' + previewImageUrl" style="width: 100%;" alt="预览">
    </el-dialog>
    <el-drawer v-model="userDrawerVisible" size="80%" :with-header="true" :destroy-on-close="true"
      :title="$t('g.detail')">
      <MemberList :propId="currentContact?.Id"></MemberList>
    </el-drawer>
  </div>
</template>

<script>
import { reactive, ref, onMounted, nextTick, computed, onUnmounted } from 'vue'
import axios from "axios"
import { ElMessage } from 'element-plus'
import i18n from '@/data/i18n/index'
import { toDay } from '@/common/func'
import MemberList from '@/views/member/list.vue'
export default {
  name: 'CustomerServiceChat',
  components: {
    MemberList
  },
  setup() {
    const t = i18n.global.t
    const defaultAvatar = require('../../assets/img/tx.png')
    const selfAvatar = require('../../assets/img/cs.png')
    const contactList = ref([])
    const currentContactId = ref(null)
    const currentContact = ref(null)
    const messageList = ref([])
    const inputMessage = ref('')
    const messageAreaRef = ref(null)
    const imageInputRef = ref(null)
    const imagePreviewVisible = ref(false)
    const previewImageUrl = ref('')
    const searchKeyword = ref('')
    const contactListRef = ref(null)

    const messageCache = ref(new Map())
    const scrollPositionCache = ref(new Map())
    const isLoadingHistory = ref(false)
    const hasMoreHistory = ref(true)
    const isInitialLoad = ref(true)
    const currentCursor = ref(0)
    const userDrawerVisible = ref(false)
    const currentPage = ref(1)
    const pageSize = ref(10)
    const totalContacts = ref(0)
    const isLoadingContacts = ref(false)
    const hasMoreContacts = ref(true)
    const isSearching = ref(false)

    let pollingTimer = null
    const POLLING_INTERVAL = 10000

    // 获取联系人列表
    const loadContactList = async (page = 1, reset = false) => {
      if (isLoadingContacts.value) return

      try {
        isLoadingContacts.value = true

        let res = await axios.get(`/admin/faq/getuserlist`, {
          params: {
            page: page,
            Keyword: searchKeyword.value.trim() || undefined
          }
        })
        console.log(res, '人员数据')

        if (reset) {
          contactList.value = [...res.List]
        } else {
          contactList.value = [...contactList.value, ...res.List]
        }

        totalContacts.value = res.Total || 0
        hasMoreContacts.value = contactList.value.length < totalContacts.value

        //默认选中第一个
        if (contactList.value.length > 0 && !currentContactId.value) {
          selectContact(contactList.value[0], 0)
        }
      } catch (error) {
        console.error('加载联系人列表失败:', error)
      } finally {
        isLoadingContacts.value = false
      }
    }
    const setContactRead = async (contact) => {
      let res = await axios.post(`/admin/faq/readingmsg`, {
        userId: currentContact.value.Id,
      })
      console.log(res, '设置已读')
    }

    //选择联系人
    const selectContact = async (contact, index) => {
      console.log('选中数据1', contact, index)
      currentContactId.value = contact.Id
      currentContact.value = contact

      // 修改为已读
      if (contact.HasUnreadMsg > 0) {
        await setContactRead()
        // 更新本地缓存的未读数
        const targetIndex = contactList.value.findIndex(c => c.Id === contact.Id)
        if (targetIndex !== -1) {
          contactList.value[targetIndex].HasUnreadMsg = 0
        }
      }

      // 重置游标状态
      currentCursor.value = 0
      hasMoreHistory.value = true
      isInitialLoad.value = true

      // 停止之前的轮询
      stopPolling()

      // 检查缓存中是否已有该联系人的消息
      if (messageCache.value.has(contact.Id)) {
        messageList.value = messageCache.value.get(contact.Id)
        console.log('使用缓存消息', contact.Id)
        await nextTick()

        const savedScrollTop = scrollPositionCache.value.get(contact.Id)
        if (savedScrollTop !== undefined && messageAreaRef.value) {
          messageAreaRef.value.scrollTop = savedScrollTop
          console.log('恢复滚动位置:', savedScrollTop)
        } else {
          scrollToBottom()
        }
        setTimeout(() => {
          isInitialLoad.value = false
        }, 500)
      } else {
        // 缓存中没有，则加载消息历史
        await loadMessageHistory(contact.Id)
        isInitialLoad.value = false
      }

      // 启动轮询
      startPolling()
    }

    // 加载消息历史
    const loadMessageHistory = async (contactId, cursor = 0) => {
      if (isLoadingHistory.value) return

      try {
        isLoadingHistory.value = true

        let res = await axios.get(`/admin/faq/getchatlist`, {
          params: {
            UserId: contactId,
            cursor: cursor,
          }
        })
        console.log(res, '聊天数据')

        res.List.map((item) => {
          item.CreatedAtStr = toDay(item.CreatedAt)
          item.UpdatedAtStr = toDay(item.UpdatedAt)
        })

        currentCursor.value = res.NextCursor || 0
        hasMoreHistory.value = res.HasMore || false

        if (cursor === 0) {
          messageList.value = res.List || []
        } else {
          messageList.value = [...(res.List || []), ...messageList.value]
        }

        messageCache.value.set(contactId, [...messageList.value])

        console.log(res.List[0].SenderId.includes('user'), '消息数据2222')
        await nextTick()
        if (cursor === 0) {
          scrollToBottom()
        } else {
          maintainScrollPosition()
        }
      } catch (error) {
        console.error('加载消息历史失败:', error)
        ElMessage.error('加载消息失败')
      } finally {
        isLoadingHistory.value = false
      }
    }
    // 加载更多历史消息
    const loadMoreHistory = async () => {
      console.log('再次触发111')
      if (!hasMoreHistory.value || isLoadingHistory.value || !currentContact.value) {
        return
      }

      await loadMessageHistory(currentContact.value.Id, currentCursor.value)
    }

    // const handleSearch = () => {
    //   currentPage.value = 1
    //   isSearching.value = true
    //   loadContactList(1, true)
    // }

    const handleContactListScroll = async (event) => {
      if (searchKeyword.value.trim()) {
        return
      }

      const element = event.target
      const scrollTop = element.scrollTop
      const scrollHeight = element.scrollHeight
      const clientHeight = element.clientHeight

      // 当滚动到底部附近时（距离底部50px以内）
      if (scrollHeight - scrollTop - clientHeight < 50 &&
        hasMoreContacts.value &&
        !isLoadingContacts.value) {
        currentPage.value++
        await loadContactList(currentPage.value, false)
      }
    }

    // 启动定时轮询
    const startPolling = () => {
      stopPolling() // 先清除已有的定时器
      pollingTimer = setInterval(async () => {
        if (!currentContact.value || isLoadingHistory.value) return

        try {
          let res = await axios.get(`/admin/faq/getchatlist`, {
            params: {
              UserId: currentContact.value.Id,
              cursor: 0,
            }
          })

          res.List.map((item) => {
            item.CreatedAtStr = toDay(item.CreatedAt)
            item.UpdatedAtStr = toDay(item.UpdatedAt)
          })

          // 比较消息数量，如果有新消息才更新
          const currentMessageID = messageList.value[messageList.value.length - 1]?.Id
          let newMessageId = 0
          if (res.List?.length > 0) {
            newMessageId = res.List[res.List.length - 1].Id
          }
          console.log('新消息数量', currentMessageID, newMessageId)
          if (newMessageId > currentMessageID) {
            console.log("进入新消息")
            const currentScrollTop = messageAreaRef.value?.scrollTop || 0
            const isNearBottom = messageAreaRef.value && (messageAreaRef.value.scrollHeight - currentScrollTop - messageAreaRef.value.clientHeight < 100)

            const existingIds = new Set(messageList.value.map(msg => msg.Id))
            const newMessages = (res.List || []).filter(msg => !existingIds.has(msg.Id))

            if (newMessages.length > 0) {
              messageList.value = [...messageList.value, ...newMessages]

              // 更新游标
              currentCursor.value = res.NextCursor || 0
              hasMoreHistory.value = res.HasMore || false
              messageCache.value.set(currentContact.value.Id, [...messageList.value])

              // 更新联系人的最后消息时间
              const lastMsg = newMessages[newMessages.length - 1]
              const targetIndex = contactList.value.findIndex(c => c.Id === currentContact.value.Id)
              if (targetIndex !== -1) {
                contactList.value[targetIndex].LastTime = lastMsg.CreatedAt
                contactList.value[targetIndex].LastMsg = lastMsg.MsgType === 2 ? '[图片]' : lastMsg.Msg
              }
              await setContactRead()
              await nextTick()
              if (isNearBottom) {
                scrollToBottom()
              } else {
                ElMessage.info(`收到 ${newMessages.length} 条新消息`)
              }
            }
          }
        } catch (error) {
          console.error('轮询加载消息失败:', error)
        }
      }, POLLING_INTERVAL)
      console.log('启动消息轮询，间隔:', POLLING_INTERVAL / 1000, '秒')
    }

    // 停止定时轮询
    const stopPolling = () => {
      if (pollingTimer) {
        clearInterval(pollingTimer)
        pollingTimer = null
        console.log('停止消息轮询')
      }
    }

    // 保持滚动位置（加载历史消息后）
    const maintainScrollPosition = () => {
      if (messageAreaRef.value) {
        const scrollHeight = messageAreaRef.value.scrollHeight
        const scrollTop = messageAreaRef.value.scrollTop
        setTimeout(() => {
          messageAreaRef.value.scrollTop = messageAreaRef.value.scrollHeight - scrollHeight + scrollTop
        }, 0)
      }
    }

    const handleScroll = async (event) => {
      const element = event.target

      if (currentContactId.value) {
        scrollPositionCache.value.set(currentContactId.value, element.scrollTop)
      }

      if (isInitialLoad.value) {
        return
      }
      if (element.scrollTop < 50 && hasMoreHistory.value && !isLoadingHistory.value) {
        await loadMoreHistory()
      }
    }

    // 发送消息
    const sendMessage = async () => {
      const content = inputMessage.value.trim()
      if (!content || !currentContact.value) return
      let res = await axios.post('/admin/faq/sendmsg', {
        userId: currentContact.value.Id,
        Msg: content,
        MsgType: 1
      })
      console.log(res, '发送数据')
      if (res.Id) {
        res.CreatedAtStr = toDay(res.CreatedAt)
        messageList.value.push(res)
        const targetIndex = contactList.value.findIndex(c => c.Id === currentContact.value.Id)
        if (targetIndex !== -1) {
          contactList.value[targetIndex].LastTime = res.CreatedAt
          contactList.value[targetIndex].LastMsg = content
        }

      }
      inputMessage.value = ''
      await nextTick()
      scrollToBottom()
      messageCache.value.set(currentContact.value.Id, [...messageList.value])
    }

    const triggerImageUpload = () => {
      imageInputRef.value?.click()
    }

    const handleImageSelect = async (event) => {
      const file = event.target.files?.[0]
      console.log(file, '图片数据')
      if (!file || !currentContact.value) return

      if (!file.type.startsWith('image/')) {
        ElMessage.warning('请选择图片文件')
        return
      }

      if (file.size > 5 * 1024 * 1024) {
        ElMessage.warning('图片大小不能超过5MB')
        return
      }

      try {
        const formData = new FormData()
        formData.append('userId', currentContact.value.Id)
        formData.append('image', file)

        let res = await axios.post('/admin/faq/sendimagemsg', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        console.log(res, '图片数据')
        if (!res.Id) return
        res.CreatedAtStr = toDay(res.CreatedAt)
        messageList.value.push(res)
        // 更新缓存
        messageCache.value.set(currentContact.value.Id, [...messageList.value])

        await nextTick()
        scrollToBottom()
        updateContactLastMessage(currentContact.value, '[图片]')
        ElMessage.success('图片发送成功')
      } catch (error) {
        console.error('上传图片失败:', error)
        ElMessage.error('上传图片失败')
      } finally {
        event.target.value = ''
      }
    }

    const previewImage = (imageUrl) => {
      previewImageUrl.value = imageUrl
      imagePreviewVisible.value = true
    }

    const updateContactLastMessage = (contact, message) => {
      contact.LastMsg = message
      contact.updateTime = new Date().toISOString()

      const index = contactList.value.findIndex(c => c.Id === contact.Id)
      if (index > -1) {
        contactList.value.splice(index, 1)
        contactList.value.unshift(contact)
      }
    }

    const scrollToBottom = () => {
      if (messageAreaRef.value) {
        messageAreaRef.value.scrollTop = messageAreaRef.value.scrollHeight
      }
    }

    const formatMessageTime = (timeStr) => {
      if (!timeStr) return ''

      let timestamp = timeStr
      if (typeof timeStr === 'string' && /^\d+$/.test(timeStr)) {
        timestamp = parseInt(timeStr)
      }

      if (timestamp < 10000000000) {
        timestamp = timestamp * 1000
      }

      const msgDate = new Date(timestamp)
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      const msgTimeOnly = new Date(msgDate.getFullYear(), msgDate.getMonth(), msgDate.getDate())

      if (msgTimeOnly.getTime() === today.getTime()) {
        const hours = String(msgDate.getHours()).padStart(2, '0')
        const minutes = String(msgDate.getMinutes()).padStart(2, '0')
        return `${hours}:${minutes}`
      }

      if (msgTimeOnly.getTime() === yesterday.getTime()) {
        return '昨天'
      }

      const diffDays = Math.floor((today.getTime() - msgTimeOnly.getTime()) / (1000 * 60 * 60 * 24))
      if (diffDays < 7) {
        const weekDays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
        return weekDays[msgDate.getDay()]
      }

      const month = String(msgDate.getMonth() + 1).padStart(2, '0')
      const day = String(msgDate.getDate()).padStart(2, '0')
      return `${month}/${day}`
    }

    const filteredContactList = computed(() => {
      if (!searchKeyword.value.trim()) {
        return contactList.value
      }
      const keyword = searchKeyword.value.trim().toLowerCase()
      return contactList.value.filter(contact =>
        contact.Nickname && contact.Nickname.toLowerCase().includes(keyword)
      )
    })

    onMounted(() => {
      loadContactList(1, true)
      if (messageAreaRef.value) {
        messageAreaRef.value.addEventListener('scroll', handleScroll)
      }
    })

    onUnmounted(() => {
      if (messageAreaRef.value) {
        messageAreaRef.value.removeEventListener('scroll', handleScroll)
      }
      stopPolling()
    })


    return {
      contactList,
      currentContactId,
      currentContact,
      messageList,
      inputMessage,
      messageAreaRef,
      contactListRef,
      imageInputRef,
      imagePreviewVisible,
      previewImageUrl,
      defaultAvatar,
      selfAvatar,
      selectContact,
      sendMessage,
      triggerImageUpload,
      handleImageSelect,
      previewImage,
      searchKeyword,
      filteredContactList,
      isLoadingHistory,
      hasMoreHistory,
      isLoadingContacts,
      hasMoreContacts,
      handleContactListScroll,
      formatMessageTime,
      userDrawerVisible
    }
  }
}
</script>

<style lang="scss" scoped>
.content {
  box-sizing: border-box;
  padding: 20px;
  height: calc(100vh - 60px);
  overflow: hidden;
  background-color: #f0f2f5;
  display: flex;
  justify-content: center;
  align-items: center;
}

.chat_container {
  width: 90%;
  max-width: 1400px;
  height: 85vh;
  min-height: 600px;
  display: flex;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.chat_sidebar {
  width: 320px;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  background-color: #fff;

  .sidebar_header {
    padding: 20px 16px 12px;
    border-bottom: 1px solid #f0f0f0;

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #1a1a1a;
    }
  }

  .search_box {
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;
    background-color: #fff;

    :deep(.el-input__wrapper) {
      box-shadow: none;
      background-color: #f5f5f5;
      border-radius: 20px;
      transition: all 0.3s;

      &:hover {
        background-color: #ebebeb;
      }

      &.is-focus {
        background-color: #fff;
        box-shadow: 0 0 0 1px #409eff inset;
      }
    }
  }

  .contact_list {
    flex: 1;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #d9d9d9;
      border-radius: 3px;

      &:hover {
        background-color: #bfbfbf;
      }
    }
  }

  .loading_contacts,
  .no_more_contacts {
    text-align: center;
    padding: 12px 0;
    font-size: 13px;
    color: #999;
  }

  .loading_contacts {
    color: #409eff;
  }

  .no_result {
    text-align: center;
    padding: 40px 0;
    color: #999;
    font-size: 14px;
  }

  .contact_item {
    display: flex;
    align-items: center;
    padding: 14px 16px;
    cursor: pointer;
    transition: all 0.2s ease;
    border-bottom: none;
    position: relative;

    &:hover {
      background-color: #f5f7fa;
    }

    &.active {
      background-color: #e8f4ff;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 3px;
        background-color: #409eff;
      }
    }

    .contact_avatar {
      position: relative;
      margin-right: 12px;
      flex-shrink: 0;

      img {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        object-fit: cover;
        border: 2px solid #fff;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      }

      .unread_badge {
        position: absolute;
        top: -4px;
        right: -4px;
        min-width: 20px;
        height: 20px;
        background: linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%);
        color: #fff;
        font-size: 11px;
        font-weight: 600;
        border-radius: 10px;
        text-align: center;
        box-shadow: 0 2px 6px rgba(255, 77, 79, 0.4);
        border: 2px solid #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 4px;
      }
    }

    .contact_info {
      flex: 1;
      overflow: hidden;
      min-width: 0;

      .contact_name_row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 6px;

        .contact_name {
          font-size: 15px;
          font-weight: 500;
          color: #1a1a1a;
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .contact_time {
          font-size: 12px;
          color: #999;
          flex-shrink: 0;
          margin-left: 8px;
        }
      }

      .contact_last_msg {
        font-size: 13px;
        color: #999;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        line-height: 1.4;
      }
    }
  }
}

.chat_main {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;
}

.chat_header {
  padding: 16px 24px;
  border-bottom: 1px solid #e8e8e8;
  background-color: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);

  .header_info {
    display: flex;
    align-items: center;

    .header_avatar {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      margin-right: 12px;
      object-fit: cover;
      border: 2px solid #fff;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .header_text {
      .header_name {
        font-size: 17px;
        font-weight: 600;
        color: #1a1a1a;
        margin-bottom: 2px;
      }

      .belonging {
        font-size: 12px;
      }
    }
  }
}

.message_area {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background-color: #f5f7fa;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #d9d9d9;
    border-radius: 3px;

    &:hover {
      background-color: #bfbfbf;
    }
  }

  .empty_state {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: #999;
    font-size: 15px;
  }

  .message_list {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .loading_more,
  .no_more_history {
    text-align: center;
    padding: 12px 0;
    font-size: 13px;
    color: #999;
  }

  .loading_more {
    color: #409eff;
  }

  .message_item {
    display: flex;
    gap: 12px;
    max-width: 65%;

    &.message_self {
      align-self: flex-end;
      flex-direction: row-reverse;

      .message_content {
        align-items: flex-end;

        .message_info {
          flex-direction: row-reverse;
        }

        .message_bubble {
          background: #409eff;
          color: #fff;
          box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);

          &::before {
            right: -8px;
            left: auto;
            border-width: 8px 0 8px 10px;
            border-left-color: #409eff;
            border-right-color: transparent;
            border-top-color: transparent;
            border-bottom-color: transparent;
          }
        }
      }
    }

    &.message_other {
      align-self: flex-start;

      .message_bubble {
        background-color: #fff;
        color: #1a1a1a;
        border: 1px solid #e8e8e8;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

        &::before {
          left: -8px;
          right: auto;
          border-width: 8px 10px 8px 0;
          border-left-color: transparent;
          border-right-color: #fff;
          border-top-color: transparent;
          border-bottom-color: transparent;
        }
      }
    }

    .message_avatar {
      flex-shrink: 0;

      img {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        object-fit: cover;
        border: 2px solid #fff;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
      }
    }

    .message_content {
      display: flex;
      flex-direction: column;
      gap: 6px;

      .message_info {
        display: flex;
        gap: 8px;
        font-size: 12px;
        color: #999;

        .message_name {
          font-weight: 500;
        }
      }

      .message_bubble {
        position: relative;
        padding: 12px 16px;
        border-radius: 12px;
        font-size: 14px;
        line-height: 1.6;
        word-break: break-word;
        max-width: 500px;

        &::before {
          content: '';
          position: absolute;
          top: 10px;
          width: 0;
          height: 0;
          border-style: solid;
        }
      }

      .message_image {
        max-width: 320px;
        border-radius: 12px;
        overflow: hidden;
        cursor: pointer;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        transition: transform 0.2s ease;

        &:hover {
          transform: scale(1.02);
        }

        img {
          width: 100%;
          height: auto;
          display: block;
        }
      }
    }
  }
}

.input_area {
  border-top: 1px solid #e8e8e8;
  background-color: #fff;
  padding: 16px 24px 20px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);

  .input_toolbar {
    margin-bottom: 12px;
    display: flex;
    gap: 8px;

    :deep(.el-button) {
      border-radius: 6px;
      font-size: 13px;
      padding: 8px 16px;

      i {
        margin-right: 4px;
      }
    }
  }

  .input_box {
    textarea {
      width: 100%;
      max-width: 100%;
      padding: 12px 16px;
      border: 1px solid #d9d9d9;
      border-radius: 8px;
      font-size: 14px;
      line-height: 1.6;
      resize: none;
      outline: none;
      transition: all 0.3s;
      box-sizing: border-box;
      background-color: #fafafa;

      &:focus {
        border-color: #409eff;
        background-color: #fff;
        box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
      }

      &::placeholder {
        color: #bfbfbf;
      }
    }
  }

  .input_footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 12px;

    .tip_text {
      font-size: 12px;
      color: #999;
    }

    :deep(.el-button) {
      border-radius: 6px;
      padding: 10px 24px;
      font-weight: 500;
    }
  }
}

@media screen and (max-width: 1400px) {
  .chat_container {
    width: 95%;
    height: 88vh;
  }
}

@media screen and (max-width: 1200px) {
  .chat_container {
    width: 98%;
    height: 90vh;
  }

  .chat_sidebar {
    width: 280px;
  }

  .message_item {
    max-width: 75% !important;
  }
}

@media screen and (max-width: 800px) {
  .content {
    padding: 10px;
  }

  .chat_container {
    width: 100%;
    height: 95vh;
    border-radius: 8px;
  }

  .chat_sidebar {
    width: 220px;
  }

  .message_item {
    max-width: 85% !important;
  }

  .chat_header {
    padding: 12px 16px;
  }

  .message_area {
    padding: 16px;
  }

  .input_area {
    padding: 12px 16px 16px;
  }
}
</style>
<!-- 

-->
